import React from "react";
import { Code, FileSearch, Search, FileText, Network, ShieldCheck, MapPin, Compass, Globe, ShoppingBag, Building2, Sparkles, ArrowRight, type LucideIcon } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface ServiceItem {
  title: string;
  description: string;
  icon: LucideIcon;
  to?: string;
  linkLabel?: string;
}
const services: ServiceItem[] = [{
  title: "Technical SEO",
  description: "Crawlability, indexation, canonicals, site speed, redirects and XML sitemaps to ensure search engines navigate and evaluate your website efficiently.",
  icon: Code,
  to: "/TechnicalSeoPage",
  linkLabel: "Explore technical SEO"
}, {
  title: "SEO Audits",
  description: "A commercial diagnosis rather than an automated export, delivering a prioritised action plan alongside competitor research and keyword gap analysis.",
  icon: FileSearch,
  to: "/SeoAuditsPage",
  linkLabel: "Explore SEO audits"
}, {
  title: "Keyword Research",
  description: "Commercial search intent, keyword mapping, buyer-focused searches and competitor analysis to target phrases that attract qualified customers.",
  icon: Search
}, {
  title: "On-Page SEO",
  description: "Metadata, content optimisation, headings, internal linking and landing pages tailored to engage human readers while satisfying search engines.",
  icon: FileText
}, {
  title: "Website Architecture",
  description: "Logical site structure, topical relevance and navigation designed to distribute link equity cleanly and guide visitors to key conversion points.",
  icon: Network
}, {
  title: "Authority Building",
  description: "Relevant backlinks, digital PR opportunities and sustainable link acquisition, strictly adhering to search guidelines with no manipulative schemes.",
  icon: ShieldCheck
}, {
  title: "Local SEO",
  description: "Optimise your Google Business Profile, local landing pages and regional citations to capture relevant searches across your target geographic locations.",
  icon: MapPin,
  to: "/LocalSEOPage",
  linkLabel: "Explore local SEO"
}, {
  title: "National SEO",
  description: "Commercial keyword research, search intent mapping and topic clusters across key category and service pages for competitive UK-wide visibility.",
  icon: Compass,
  to: "/NationalSeoPage",
  linkLabel: "Explore national SEO"
}, {
  title: "International SEO",
  description: "Hreflang implementation, country and language targeting, and regional localisation so international searchers find the correct version of your site.",
  icon: Globe,
  to: "/InternationalSeoPage",
  linkLabel: "Explore international SEO"
}, {
  title: "Ecommerce SEO",
  description: "Category and product page optimisation, faceted navigation management, solving duplicate filter URLs and configuring structured data markup.",
  icon: ShoppingBag,
  to: "/EcommerceSeoPage",
  linkLabel: "Explore ecommerce SEO"
}, {
  title: "B2B SEO",
  description: "Targeting decision-maker queries with tailored service pages and supporting resources designed to generate qualified enquiries through long buying cycles.",
  icon: Building2,
  to: "/B2BSeoLeadGenerationPage",
  linkLabel: "Explore B2B SEO"
}, {
  title: "AI Search Optimisation",
  description: "Structuring clear answers, rich schema, topical depth and entity clarity on top of strong traditional SEO foundations without misleading citation promises.",
  icon: Sparkles,
  to: "/AISearchOptimisationPage",
  linkLabel: "Explore AI search optimisation"
}];
export default function Services() {
  return <section id="services" aria-labelledby="services-heading" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/40">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14 lg:mb-16">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary mb-3">
            Core Areas
          </p>
          <h2 id="services-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
            What I Can Help You With
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            As an independent SEO consultant, I advise across every critical dimension of search to help your business achieve durable organic performance and attract high-value enquiries.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map(service => {
          const Icon = service.icon;
          return <Card key={service.title} className="h-full flex flex-col justify-between bg-card border-border shadow-xs hover:border-primary/40 hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-3">
                  <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle>
                    <h3 className="font-serif text-lg font-bold text-card-foreground">
                      {service.title}
                    </h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 pb-4">
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
                {service.to && service.linkLabel && <CardFooter className="pt-2 border-t border-border/60">
                    <Link to={service.to} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                      <span>{service.linkLabel}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </CardFooter>}
              </Card>;
        })}
        </div>
      </div>
    </section>;
}